import SaveIcon from '@mui/icons-material/Check'
import UndoIcon from '@mui/icons-material/Undo'
import PreviewIcon from '@mui/icons-material/Visibility'
import Checkbox from '@mui/material/Checkbox'
import Fab from '@mui/material/Fab'
import FormControlLabel from '@mui/material/FormControlLabel'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import AvatarUploader from 'components/AvatarUploader'
import * as React from 'react'
import { PPLimit } from 'shared'
import { BYTES_IN_GB, BYTES_IN_MB, LIMIT_MY_LIBRARY_STORAGE, LIMIT_UNLIMITED } from 'shared/constants'

import styles from '../FeaturesManager.pcss'
const BASE_TEN = 10
const SIZE_PRECISION = 2

/** Last known finite value per plan, restored when "unlimited" is unchecked */
function buildLastFiniteValues(limit: PPLimit): Record<string, number> {
  return Object.keys(limit.plans).reduce((map: Record<string, number>, plan: string) => {
    const value = limit.plans[plan]
    map[plan] = value === LIMIT_UNLIMITED ? 0 : value
    return map
  }, {})
}

export interface LimitTableRowProps {
  limit: PPLimit
  plans: Record<string, string>
  onUpdate: (limit: PPLimit, file: File | null, order?: number) => void
  onFileUploadError: (error: string) => void
  onPreviewClick: (key: string, value: number) => void
}

export function LimitTableRow(props: LimitTableRowProps) {
  const [refreshKey, setRefreshKey] = React.useState(0)
  const [data, setData] = React.useState<PPLimit>(props.limit)
  const [file, setFile] = React.useState<File | null>(null)
  const [edited, setEdited] = React.useState(false)
  const [limitsByPlan, setLimitsByPlan] = React.useState<Record<string, { isUnlimited: boolean, value: number }>>(
    Object.keys(props.limit.plans).reduce((map: Record<string, { isUnlimited: boolean, value: number }>, plan: string) => {
      map[plan] = {
        isUnlimited: props.limit.plans[plan] === -1,
        value: props.limit.plans[plan]
      }
      return map
    }, {})
  )
  const [lastFiniteValues, setLastFiniteValues] = React.useState<Record<string, number>>(() => buildLastFiniteValues(props.limit))
  const nameRef = React.useRef<HTMLDivElement>(null)
  const upgradeButtonTextRef = React.useRef<HTMLDivElement>(null)
  const dismissPopupTextRef = React.useRef<HTMLDivElement>(null)
  const salesPitchRef = React.useRef<HTMLDivElement>(null)
  const mobileCtaRef = React.useRef<HTMLDivElement>(null)
  const mobileSalesPitchRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    setEdited(false)
    setData(props.limit)
    setLastFiniteValues(buildLastFiniteValues(props.limit))
  }, [props.limit])

  const onUnlimitedCheckboxChange = (plan: string) => () => {
    setEdited(true)
    // The checkbox reads `data.plans`, so the toggle only has to update it. `lastFiniteValues`
    // is never written here, so no second state slice can drift out of sync with `data`.
    setData(current => ({
      ...current,
      plans: {
        ...current.plans,
        [plan]: current.plans[plan] === LIMIT_UNLIMITED ? (lastFiniteValues[plan] ?? 0) : LIMIT_UNLIMITED
      }
    }))
  }

  const onStorageLimitChange = (plan: string) => (bytes: number) => {
    setEdited(true)
    setData(current => ({
      ...current,
      plans: {
        ...current.plans,
        [plan]: bytes
      }
    }))
    setLastFiniteValues(current => ({ ...current, [plan]: bytes }))
  }

  const onLimitChange = (plan: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setEdited(true)
    // An emptied field parses to NaN, which would otherwise be saved as the string "NaN"
    const value = parseInt(e.target.value, BASE_TEN) || 0
    setData(current => ({
      ...current,
      plans: {
        ...current.plans,
        [plan]: value
      }
    }))
    setLastFiniteValues(current => ({ ...current, [plan]: value }))
  }

  const renderLimitValue = (plan: string) => {
    // A product can exist in `props.plans` without an entry in this limit's `plans` map
    const value = data.plans[plan] ?? 0
    const isUnlimited = value === LIMIT_UNLIMITED
    const isStorage = props.limit.key === LIMIT_MY_LIBRARY_STORAGE

    return (
      <React.Fragment>
        {!isUnlimited && (
          isStorage
            ? <StorageSizeInput bytes={value} onChange={onStorageLimitChange(plan)} />
            : (
              <input
                className={styles.input}
                type="number"
                value={value}
                min={0}
                onChange={onLimitChange(plan)}
              />
            )
        )}
        <FormControlLabel
          label="unlimited"
          classes={{ label: styles['cb-label'] }}
          control={(
            <Checkbox
              checked={isUnlimited}
              color="primary"
              size="small"
              onChange={onUnlimitedCheckboxChange(plan)}
            />
          )}
        />
      </React.Fragment>
    )
  }

  const undoChanges = () => {
    setEdited(false)
    setData(props.limit)
    setLastFiniteValues(buildLastFiniteValues(props.limit))
    setRefreshKey(current => current + 1)
  }

  const handleImageUploaded = (file: File) => {
    setFile(file)
    setEdited(true)
  }

  const handleInput = () => {
    setEdited(true)
  }

  const saveChanges = () => {
    // Every product in `props.plans` renders an editable value - 0 when this limit has no entry
    // for it - so send all of them rather than only the keys that happened to arrive
    const plans = Object.keys(props.plans).reduce((map: Record<string, number>, plan: string) => {
      map[plan] = data.plans[plan] ?? 0
      return map
    }, {})

    const draft: PPLimit = {
      id: props.limit.id,
      key: props.limit.key,
      name: nameRef.current?.innerText.trim() as string,
      salesPitch: salesPitchRef.current?.innerText.trim() as string,
      upgradePopupDismissText: dismissPopupTextRef.current?.innerText.trim() as string,
      upgradeActionText: upgradeButtonTextRef.current?.innerText.trim() as string,
      plans,
      image: '',
      isLimit: true,
      mobileCta: mobileCtaRef.current?.innerText.trim() as string,
      mobileSalesPitch: mobileSalesPitchRef.current?.innerText.trim() as string,
      visibility: data.visibility,
      order: props.limit.order // NOTE: Pass previous order to be used for reordering if needed
    }

    const updateOrder = data.order !== props.limit.order
    props.onUpdate(draft, file, updateOrder ? data.order : undefined)
  }

  const openUpgradeDialog = () => {
    const planValues = Object.values(data.plans || {})
    if (planValues.length === 0) {
      return
    }
    const maxValue = Math.max(...planValues)
    props.onPreviewClick(props.limit.key, maxValue + 1)
  }

  return (
    <TableRow className={styles.tr} key={refreshKey}>
      <TableCell className={styles['cell-image']}>
        <AvatarUploader
          imageUrl={props.limit.image}
          labelAdd="ADD IMAGE"
          labelChange="CHANGE IMAGE"
          className={styles.uploader}
          onFileUploaded={handleImageUploaded}
          onError={props.onFileUploadError}
        />
        <a href={props.limit.image} target="_blank" className={styles['link-img']}>see full size</a>
      </TableCell>
      <TableCell>
        <div
          contentEditable
          suppressContentEditableWarning
          ref={nameRef}
          data-key="name"
          className={`${styles['cell-textarea']} ${styles['cell-md']}`}
          onInput={handleInput}
        >
          {data.name}
        </div>
      </TableCell>
      <TableCell>
        <div
          contentEditable
          suppressContentEditableWarning
          ref={salesPitchRef}
          data-key="sales-pitch"
          className={`${styles['cell-textarea']} ${styles['cell-wide']}`}
          onInput={handleInput}
        >
          {data.salesPitch}
        </div>
      </TableCell>
      <TableCell>
        <div
          contentEditable
          suppressContentEditableWarning
          ref={mobileSalesPitchRef}
          data-key="mobile-sales-pitch"
          className={`${styles['cell-textarea']} ${styles['cell-wide']}`}
          onInput={handleInput}
        >
          {data.mobileSalesPitch}
        </div>
      </TableCell>
      <TableCell>
        <div
          contentEditable
          suppressContentEditableWarning
          ref={mobileCtaRef}
          data-key="mobile-cta"
          className={styles['cell-textarea']}
          onInput={handleInput}
        >
          {data.mobileCta}
        </div>
      </TableCell>
      <TableCell>
        <div
          contentEditable
          suppressContentEditableWarning
          ref={dismissPopupTextRef}
          data-key="dismiss-popup-text"
          className={`${styles['cell-textarea']} ${styles['cell-md']}`}
          onInput={handleInput}
        >
          {data.upgradePopupDismissText}
        </div>
      </TableCell>
      <TableCell>
        <div
          contentEditable
          suppressContentEditableWarning
          ref={upgradeButtonTextRef}
          data-key="upgrade-action-text"
          className={styles['cell-textarea']}
          onInput={handleInput}
        >
          {data.upgradeActionText}
        </div>
      </TableCell>
      {Object.keys(props.plans).map(id => (
        <TableCell className={styles['td-number']} key={id}>
          {renderLimitValue(id)}
        </TableCell>
      ))}
      <TableCell className={styles['cell-actions']}>
        <Tooltip title="Save" placement="top">
          <span>
            <Fab size="small" color="primary" disabled={!edited} onClick={saveChanges}>
              <SaveIcon />
            </Fab>
          </span>
        </Tooltip>
        <Tooltip title="Undo" placement="top">
          <span>
            <Fab size="small" disabled={!edited} onClick={undoChanges}>
              <UndoIcon fontSize="small" />
            </Fab>
          </span>
        </Tooltip>
        <Tooltip title="Open upgrade dialog" placement="top">
          <span>
            <Fab size="small" color="primary" onClick={openUpgradeDialog}>
              <PreviewIcon fontSize="small" />
            </Fab>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  )
}

export default LimitTableRow

function StorageSizeInput(props: { bytes: number, onChange: (size: number) => void }) {
  const bytes = props.bytes > 0 ? props.bytes : 0
  // The unit is the admin's entry unit, not a formatting of the current value. Deriving it from
  // the byte count would flip GB back to MB the moment the field is cleared - so retyping "5"
  // would save 5 MB - and would leave GB unpickable while the value sits at 0.
  // Anything below 1 GB reads better in MB, so that is where it starts.
  const [suffix, setSuffix] = React.useState(() => (bytes >= BYTES_IN_GB ? 'GB' : 'MB'))
  const multiplier = suffix === 'GB' ? BYTES_IN_GB : BYTES_IN_MB
  // Derived from the byte count rather than a formatted string, so a fractional
  // cap such as 1.5 GB survives a round trip through the input
  const value = Number((bytes / multiplier).toFixed(SIZE_PRECISION))

  const onNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // An emptied field parses to NaN, which would otherwise be saved as the string "NaN"
    const newValue = parseFloat(e.target.value) || 0
    props.onChange(newValue * multiplier)
  }

  const onSuffixChange = (e: SelectChangeEvent) => {
    const nextSuffix = e.target.value === 'GB' ? 'GB' : 'MB'
    setSuffix(nextSuffix)
    // Re-read the number in the newly picked unit off the unrounded byte count. Using the
    // displayed `value` would rewrite any cap that does not fit in two decimals of the unit.
    props.onChange((bytes / multiplier) * (nextSuffix === 'GB' ? BYTES_IN_GB : BYTES_IN_MB))
  }

  return (
    <div className={styles['storage-box']}>
      <input type="number" value={value} step="any" className={styles.input} min={0} onChange={onNumberChange} />
      <Select value={suffix} onChange={onSuffixChange} classes={{ select: styles['select-input'] }}>
        <MenuItem value="MB">MB</MenuItem>
        <MenuItem value="GB">GB</MenuItem>
      </Select>
    </div>
  )
}
